import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Box,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import {
  UninvoicedPayment,
  InvoiceResult,
} from '../../../services/uninvoicedPaymentsService';
import { useInvoicePayments, useUninvoicedPayments } from 'src/queries/admin';

const summarise = (results: InvoiceResult[]): string => {
  const count = (status: InvoiceResult['status']) => results.filter((r) => r.status === status).length;
  return `Invoiced ${count('invoiced')}, reused ${count('reused')}, failed ${count('failed')}.`;
};

const UninvoicedPaymentsPage: React.FC = () => {
  const [actingId, setActingId] = useState<number | null>(null);
  const [invoicingAll, setInvoicingAll] = useState(false);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data: payments = [], isPending, isError } = useUninvoicedPayments();
  const invoicePayments = useInvoicePayments();

  const error = isError
    ? 'Failed to load uninvoiced payments. Please try again later.'
    : actionError;

  /**
   * Successfully invoiced payments leave the list, which the mutation handles by
   * invalidating it — the server decides what is still uninvoiced rather than the
   * page splicing its own array. The per-payment results are still needed here
   * for the summary and the partial-failure message.
   */
  const applyResults = (results: InvoiceResult[]) => {
    setSuccess(summarise(results));
    const failed = results.filter((r) => r.status === 'failed');
    setActionError(
      failed.length
        ? `${failed.length} payment(s) failed to invoice — the business-alerts channel was notified.`
        : null
    );
  };

  const handleInvoiceOne = async (payment: UninvoicedPayment) => {
    setActingId(payment.id);
    try {
      const { results } = await invoicePayments.mutateAsync([payment.id]);
      applyResults(results);
    } catch (err: any) {
      setActionError(err?.response?.data?.message || 'Failed to invoice payment. Please try again.');
      console.error('Error invoicing payment:', err);
    } finally {
      setActingId(null);
    }
  };

  const handleInvoiceAll = async () => {
    setConfirmAllOpen(false);
    setInvoicingAll(true);
    try {
      const { results } = await invoicePayments.mutateAsync(undefined);
      applyResults(results);
    } catch (err: any) {
      setActionError(err?.response?.data?.message || 'Failed to invoice payments. Please try again.');
      console.error('Error invoicing payments:', err);
    } finally {
      setInvoicingAll(false);
    }
  };

  const invoiceableCount = payments.filter((p) => !p.is_test).length;

  if (isPending) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">Uninvoiced Payments</Typography>
          <Button
            variant="contained"
            color="primary"
            disabled={invoicingAll || invoiceableCount === 0}
            onClick={() => setConfirmAllOpen(true)}
          >
            {invoicingAll ? <CircularProgress size={24} /> : `Invoice all (${invoiceableCount})`}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Payments recorded but not yet invoiced in Xero, on locations that have a Xero contact.
          Re-triggering reuses an existing Xero invoice if one already exists (no duplicates).
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Merchant</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Amount (ZAR)</TableCell>
                <TableCell>PayFast ID</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell>{payment.id}</TableCell>
                  <TableCell>{payment.merchant.name}</TableCell>
                  <TableCell>
                    {payment.subscription_id != null ? 'Subscription' : 'Digital Flyer'}
                    {payment.is_test && <Chip label="TEST" size="small" color="warning" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>R {payment.amount_gross.toFixed(2)}</TableCell>
                  <TableCell>{payment.pf_payment_id}</TableCell>
                  <TableCell>
                    {new Date(payment.date_created).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleInvoiceOne(payment)}
                      disabled={actingId === payment.id || invoicingAll}
                    >
                      {actingId === payment.id ? <CircularProgress size={20} /> : 'Invoice now'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {payments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No uninvoiced payments
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <ConfirmationDialog
        isOpen={confirmAllOpen}
        title="Invoice all pending payments"
        message={`Create Xero invoices for ${invoiceableCount} pending payment(s)? Test payments are excluded. Existing invoices are reused, not duplicated.`}
        handleClose={() => setConfirmAllOpen(false)}
        handleConfirm={handleInvoiceAll}
      />

      <Snackbar
        open={!!success}
        autoHideDuration={5000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UninvoicedPaymentsPage;
